import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
// @mui
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Unstable_Grid2'
import Container from '@mui/material/Container'
// utils
// routes
import { paths } from 'src/routes/paths'
import { useParams, useRouter, useSearchParams } from 'src/routes/hook'
// types
import { IEventItem } from 'src/types/events'
// assets
// components
import { useSnackbar } from 'src/components/snackbar'
import FormProvider from 'src/components/hook-form'
import { LoadingScreen } from 'src/components/loading-screen'
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs/custom-breadcrumbs'
import axiosInstance from 'src/utils/axios'
import { ConfirmDialog } from 'src/components/custom-dialog'
import { useBoolean } from 'src/hooks/use-boolean'

import { Helmet } from 'react-helmet-async'
import { EventStepOneSchema, EventStepTwoSchema } from 'src/validators/event.schema'
import { masks } from 'src/utils/masks'
import EventStepper from './stepper-event/event-stepper'
import EventStepperOne from './stepper-event/event-stepper-one'
import EventStepperTwo from './stepper-event/event-stepper-two'
import EventStepperThree from './stepper-event/event-stepper-three'

// Helper function to convert tax values to proper decimal format
const convertTaxValues = (tax: any) => {
  if (!tax) return tax

  return {
    credit: tax.credit ? parseFloat(tax.credit.toString().replace(',', '.')) || 0 : 0,
    debit: tax.debit ? parseFloat(tax.debit.toString().replace(',', '.')) || 0 : 0,
    money: tax.money ? parseFloat(tax.money.toString().replace(',', '.')) || 0 : 0,
    pix: tax.pix ? parseFloat(tax.pix.toString().replace(',', '.')) || 0 : 0,
  }
}

// Helper function to convert currency values to cents (remove comma and convert to integer)
const convertCurrencyToCents = (value: string | number) => {
  if (!value) return 0

  // Convert to string and remove any currency formatting
  const stringValue = value.toString().replace('R$', '').replace(' ', '').trim()

  // If it has comma, convert to cents (3,85 -> 385)
  if (stringValue.includes(',')) {
    const parts = stringValue.split(',')
    const reais = parseInt(parts[0], 10) || 0
    const centavos = parseInt((parts[1] || '00').padEnd(2, '0').substring(0, 2), 10)
    return reais * 100 + centavos
  }

  // If it's already a number without comma, assume it's already in cents
  return parseInt(stringValue, 10) || 0
}

// Helper function to convert cents back to currency format for display
const convertCentsToDisplay = (cents: number) => {
  if (!cents) return '0,00'

  const reais = Math.floor(cents / 100)
  const centavos = cents % 100

  return `${reais},${centavos.toString().padStart(2, '0')}`
}

// Helper function to format payload for backend
const formatPayloadForBackend = (payload: any) => ({
  ...payload,
  tax: convertTaxValues(payload.tax),
  extraCost: convertCurrencyToCents(payload.extraCost),
})

export default function EventCreateEditView() {
  const params = useParams()
  const searchParams = useSearchParams()

  const [loading, setLoading] = useState(false)
  const [currentUser, setCurrentUser] = useState<IEventItem>()

  // Initialize activeStep from URL parameter (step=3 goes to cardápio)
  const urlStep = searchParams.get('step')
  const initialStep = urlStep ? parseInt(urlStep, 10) - 1 : 0 // Convert to 0-based index
  const [activeStep, setActiveStep] = useState(Math.max(0, Math.min(2, initialStep)))

  const [createId, setCreateId] = useState()
  const { id } = params
  const router = useRouter()
  const confirm = useBoolean()

  const { enqueueSnackbar } = useSnackbar()

  async function fetchUser(userId: string) {
    try {
      setLoading(true)

      const { data: response } = await axiosInstance.get(`/event/${userId}`)

      Object.keys(response.event).forEach((event) => {
        setValue(`${event}`, response.event[event])
      })

      setValue(
        'managers',
        response.event.managers?.map((manager: any) => ({
          label: manager?.name,
          value: manager?._id,
        })),
      )

      setValue(
        'categories',
        response.categories?.map((cat: any) => {
          const products: any = response.products?.filter((prod: any) => prod.category === cat._id)
          return {
            ...cat,
            products: products.map((prod: any) => ({
              ...prod,
              price: prod.price ? masks.currency(prod.price) : null,
              newPrice: prod.price ? masks.currency(prod.price) : null,
              cost: prod.cost ? masks.currency(prod.cost) : null,
            })),
          }
        }),
      )

      setValue('date', new Date(response.event.date))

      if (response.event.extraCost) {
        setValue('extraCost', convertCentsToDisplay(response.event.extraCost))
      }

      if (response.event.installmentConfig?.minAmountForInstallment) {
        setValue(
          'installmentConfig.minAmountForInstallment',
          convertCentsToDisplay(response.event.installmentConfig.minAmountForInstallment),
        )
      }

      setCurrentUser(response)
    } catch (error) {
      if (error?.message) {
        enqueueSnackbar(error?.message, {
          variant: 'error',
        })
      } else {
        enqueueSnackbar('Erro ao remover usuario !', {
          variant: 'error',
        })
      }
    }
    setLoading(false)
  }

  const methods = useForm<any>({
    resolver: yupResolver(activeStep === 0 ? EventStepOneSchema : EventStepTwoSchema),
    defaultValues: {
      webhook: {
        enabled: false,
        url: '',
      },
      tefConfig: {
        enabled: false,
        clientCode: '',
        softDescriptor: '',
        endereco: '',
        cidade: '',
        uf: '',
        pais: 'BRA',
        cep: '',
        mcc: '',
        cnpj: '',
        telefone: '',
        nomeCompleto: '',
        tipoPessoa: 'J',
      },
    },
  })

  const {
    setValue,
    handleSubmit,
    getValues,
    formState: { isSubmitting },
  } = methods

  const onSubmit = useCallback(async () => {
    try {
      // Step 2 (cardápio) - Just finalize and redirect (cardápio items are auto-saved)
      enqueueSnackbar('Evento finalizado com sucesso!', {
        variant: 'success',
      })

      // Redirect to events list
      router.push(paths.dashboard.events.root)
    } catch (error) {
      enqueueSnackbar(error?.message || 'Erro ao finalizar evento!', {
        variant: 'error',
      })
    }
  }, [enqueueSnackbar, router])

  const handleDelete = useCallback(
    async (eventoId: string) => {
      try {
        await axiosInstance.delete(`/events/${eventoId}`)

        enqueueSnackbar('Evento removido com sucesso !')
        router.push(paths.dashboard.events.root)
      } catch (error) {
        if (error?.message) {
          enqueueSnackbar(error?.message, {
            variant: 'error',
          })
        } else {
          enqueueSnackbar('Erro ao remover evento !', {
            variant: 'error',
          })
        }
      }
    },
    [enqueueSnackbar, router],
  )

  const renderLoading = (
    <LoadingScreen
      sx={{
        borderRadius: 1.5,
        bgcolor: 'background.default',
      }}
    />
  )

  const handleNext = useCallback(
    async (data: any) => {
      try {
        let payload: any = data || getValues()

        payload.managers = payload.managers?.length
          ? payload.managers.map((manager: any) => manager.value)
          : []

        // Format values for backend
        payload = formatPayloadForBackend(payload)

        let userId = id || createId

        if (userId) {
          // Update existing event
          await axiosInstance.put(`/event/${userId}`, payload)

          // Mensagem específica por etapa
          const stepMessages = [
            'Informações básicas salvas com sucesso!',
            'Vendedores salvos com sucesso!',
          ]
          enqueueSnackbar(stepMessages[activeStep] || 'Alteração realizada com sucesso!', {
            variant: 'success',
          })
        } else {
          // Create new event
          const { data: response } = await axiosInstance.post('/event', payload)

          if (response?._id) {
            setCreateId(response._id)
            userId = response._id

            // Update URL with the new event ID
            router.replace(`/dashboard/events/${response._id}`)

            enqueueSnackbar('Evento criado com sucesso!', {
              variant: 'success',
            })
          } else {
            throw new Error('ID do evento não foi retornado')
          }
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1)
      } catch (error) {
        enqueueSnackbar(error?.message || 'Erro ao salvar!', {
          variant: 'error',
        })
      }
    },
    [activeStep, id, createId, getValues, enqueueSnackbar, router],
  )

  const handleSave = useCallback(async () => {
    try {
      let payload: any = getValues()

      payload.managers = payload.managers?.length
        ? payload.managers.map((manager: any) => manager.value)
        : []

      // Format values for backend
      payload = formatPayloadForBackend(payload)

      const userId = id || createId

      if (!userId) {
        const { data: response } = await axiosInstance.post('/event', payload)
        if (response?._id) {
          setCreateId(response._id)
          router.replace(`/dashboard/events/${response._id}`)
        } else {
          throw new Error('ID do evento não foi retornado')
        }
      } else {
        await axiosInstance.put(`/event/${userId}`, payload)
      }

      enqueueSnackbar(
        currentUser ? 'Alteração realizada com sucesso!' : 'Evento criado com sucesso!',
      )
    } catch (error) {
      enqueueSnackbar(error?.message || 'Erro ao criar evento !', {
        variant: 'error',
      })
    }
  }, [getValues, id, enqueueSnackbar, currentUser, router, createId])

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleStepClick = (step: number) => {
    // Only allow navigation if editing existing event
    if (id) {
      setActiveStep(step)
    } else {
      // For new events, only allow forward navigation and prevent jumping to step 2 without createId
      if (step <= activeStep || (step === 2 && !createId)) {
        if (step === 2 && !createId) {
          enqueueSnackbar('Crie o evento primeiro antes de acessar o cardápio', {
            variant: 'warning',
            autoHideDuration: 3000,
          })
        }
        return
      }
      setActiveStep(step)
    }
  }

  useEffect(() => {
    if (id) {
      fetchUser(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  if (loading) return renderLoading

  return (
    <>
      <Helmet>
        <title>Eventos - Criar</title>
      </Helmet>

      <Container maxWidth='lg'>
        <CustomBreadcrumbs
          heading={id ? 'Editar Evento' : 'Criar novo Evento'}
          links={[
            {
              name: 'Ínicio',
              href: paths.dashboard.root,
            },
            {
              name: 'Eventos',
              href: paths.dashboard.events.root,
            },
            { name: currentUser?.name || 'Novo Evento' },
          ]}
          sx={{
            mb: { xs: 3, md: 5 },
          }}
        />

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid xs={12} md={12}>
              <Card sx={{ p: 3 }}>
                <Stack gap={3}>
                  <EventStepper activeStep={activeStep} onStepClick={handleStepClick} />

                  {activeStep === 0 && (
                    <EventStepperOne
                      methods={methods}
                      activeStep={activeStep}
                      handleBack={handleBack}
                      handleNext={handleNext}
                      handleSave={handleSave}
                    />
                  )}
                  {activeStep === 1 && (
                    <EventStepperTwo
                      methods={methods}
                      activeStep={activeStep}
                      handleBack={handleBack}
                      isLoading={isSubmitting}
                      handleNext={handleNext}
                      handleSave={handleSave}
                    />
                  )}
                  {activeStep === 2 && (
                    <EventStepperThree
                      createId={createId || id}
                      methods={methods}
                      activeStep={activeStep}
                      handleBack={handleBack}
                      isLoading={isSubmitting}
                      handleNext={handleNext}
                      handleSave={handleSave}
                    />
                  )}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </FormProvider>
        <ConfirmDialog
          open={confirm.value}
          onClose={confirm.onFalse}
          title='Deletar'
          content={<>Tem certeza de que deseja excluir esse evento?</>}
          action={
            <Button
              variant='contained'
              color='error'
              onClick={() => {
                if (id) handleDelete(id)
                confirm.onFalse()
              }}
            >
              Deletar
            </Button>
          }
        />
      </Container>
    </>
  )
}
